import axios from 'axios'
import { Formik, FormikHelpers } from 'formik'
import moment from 'moment'
import { BookContext, EBookStatus } from 'common/contexts/Book'
import { EColors } from 'common/styles'
import { Button } from 'components/buttons'
import { Input } from 'components/input'
import { Styled } from 'components/Styled'
import { useContext, useEffect, useState } from 'react'
import { Fade } from 'react-awesome-reveal'
import { useTranslation } from 'react-i18next'
import { Container, FormContainer } from './styled'
import { TBookingPayload, TBookProps, TPendingBooking } from './types'
import { baseURL } from 'models'
import {
  useGetDisabledDays,
  useGetRestaurants,
} from 'common/hooks/useGeRestaurants/useGeRestaurants'
import { useBookSchema } from './schema'
import { isLifecellNumber, usePhoneOtp } from './helpers'

enum EInput {
  name = 'name',
  phone = 'phone',
  guests = 'guests',
  date = 'date',
  time = 'time',
}

const initialValues: TBookProps = {
  name: '',
  phone: '',
  guests: '',
  date: '',
  time: '',
  lending: true,
}

export const Form = () => {
  const { t } = useTranslation()
  const { setStatus } = useContext(BookContext)
  const [isVisible, setIsVisible] = useState(false)
  const { restaurant } = useGetRestaurants({ revalidateOnMount: false })
  const { disabledDays } = useGetDisabledDays(restaurant?._id)

  /*create validation BookSchema*/
  const { BookSchema } = useBookSchema(disabledDays)
  const [pendingBooking, setPendingBooking] = useState<TPendingBooking | null>(
    null,
  )
  const [code, setCode] = useState('')

  const {
    step: otpStep,
    isConfirming,
    send,
    confirm,
    reset: resetOtp,
  } = usePhoneOtp({
    onVerified: async () => {
      if (!pendingBooking) return

      const { booking, actions } = pendingBooking

      try {
        const response = await axios.post(`${baseURL}/api/booking`, booking, {
          headers: {
            'Content-type': 'application/json',
          },
        })

        if (response.data) {
          setStatus(EBookStatus.success)
          actions.resetForm({ values: initialValues })
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const errorRes = error.response?.data
          if (errorRes?.message === 'Can not book this day') {
            setStatus(EBookStatus.busy)
          } else {
            setStatus(EBookStatus.error)
          }
        } else {
          console.error(error)
          setStatus(EBookStatus.error)
        }
      } finally {
        setPendingBooking(null)
        setCode('')
        resetOtp()
      }
    },
  })

  const onSubmitData = async (
    values: TBookProps,
    actions: FormikHelpers<TBookProps>,
  ) => {
    if (otpStep !== 'enterPhone') return

    const time = moment(values.time, 'HH:mm').format('HH:mm')
    const date = moment(values.date, 'YYYY-MM-DD').format('YYYY-MM-DD')
    const dateData = `${date}T${time}:00`

    const digitsPhone = values.phone.replace(/\D/g, '') // "38099..."
    const phoneE164 = `+${digitsPhone}`

    const booking: TBookingPayload = {
      name: values.name,
      phone: phoneE164,
      guests: Number(values.guests),
      date: dateData,
      restaurant: restaurant?._id || '',
      lending: true,
    }

    const ok = await send(phoneE164)
    if (!ok) {
      return
    }

    setPendingBooking({ booking, actions })
  }

  useEffect(() => {
    setTimeout(() => {
      setIsVisible(true)
    }, 0)
  }, [isVisible])

  return (
    <>
      <Container>
        <div id="recaptcha-container" hidden />
        <Formik
          initialValues={initialValues}
          validationSchema={BookSchema}
          onSubmit={onSubmitData}>
          {({ handleSubmit, handleChange, setFieldValue, values }) => {
            const digits = values?.phone.replace(/\D/g, '')
            const phoneE164 = `+${digits}`
            const isLifecell = isLifecellNumber(phoneE164)
            return (
              <FormContainer
                style={{ opacity: isVisible ? 1 : 0 }}
                onSubmit={handleSubmit}>
                {otpStep === 'enterPhone' && (
                  <Fade cascade triggerOnce delay={400}>
                    <Styled.H2 align="center" color={EColors.primary_500}>
                      {t('book.table.title')}
                    </Styled.H2>

                    <Styled.FlexWrapper direction="column" gap={48}>
                      <Fade
                        cascade
                        triggerOnce
                        direction="up"
                        delay={800}
                        duration={500}
                        damping={0.5}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                        <Input.InputField
                          name={EInput.name}
                          placeholder={t('name') as string}
                          onChange={handleChange}
                          value={values.name}
                        />
                        <Input.CustomInputField
                          mask="+38(099) 999-99-99"
                          name={EInput.phone}
                          placeholder={t('phone') as string}
                          onChange={e => {
                            const value = e.target.value || ''
                            const changedValue = value
                              .replace(/\)/g, '')
                              .replace(/\(/g, '')
                              .replace(/-/g, '')
                              .replace(/ /g, '')
                            setFieldValue('phone', changedValue)
                          }}
                        />
                        <Input.InputField
                          onChange={handleChange}
                          name={EInput.guests}
                          placeholder={t('guests.total') as string}
                          type={'number'}
                          value={values.guests}
                        />

                        <Styled.FlexWrapper gap={48} width="100%">
                          <Input.InputField
                            onChange={handleChange}
                            name={EInput.date}
                            placeholder={t('date') as string}
                            type={'date'}
                            value={values.date}
                          />
                          <Input.InputField
                            onChange={handleChange}
                            name={EInput.time}
                            placeholder={t('time') as string}
                            type={'time'}
                            value={values.time}
                          />
                        </Styled.FlexWrapper>
                        <Button.Ellipse type="submit">
                          {t('book')}
                        </Button.Ellipse>
                      </Fade>
                    </Styled.FlexWrapper>
                  </Fade>
                )}
                {otpStep === 'enterCode' && (
                  <>
                    <Styled.PBody2 align="center">
                      {t('otp.sent.to')}{' '}
                      <strong>+{values.phone.replace(/\D/g, '')}</strong>
                    </Styled.PBody2>

                    <Input.InputField
                      name="otp"
                      placeholder={t('otp.code') as string}
                      value={code}
                      onChange={e => setCode(e.target.value)}
                    />

                    {/* {renderOtpError()} */}

                    <Styled.FlexWrapper gap={16}>
                      <Button.Ellipse
                        type="button"
                        onClick={() => {
                          resetOtp()
                          setPendingBooking(null)
                          setCode('')
                        }}>
                        {t('otp.changePhone')}
                      </Button.Ellipse>

                      <Button.Ellipse
                        type="button"
                        onClick={() =>
                          confirm(code, `+${values.phone.replace(/\D/g, '')}`)
                        }
                        disabled={
                          isConfirming || code.length !== (isLifecell ? 5 : 6)
                        }>
                        {isConfirming ? t('otp.confirming') : t('otp.confirm')}
                      </Button.Ellipse>
                    </Styled.FlexWrapper>
                  </>
                )}
              </FormContainer>
            )
          }}
        </Formik>
      </Container>
    </>
  )
}
